import { logError } from "@lib/logger";
import { notifyConnectionLost, notifyReconnected, notifyReconnecting } from "./ConnectionStatusNotifier";
import { WebSocketDiagnostics } from "./WebSocketDiagnostics";

export enum HeartbeatMessageType {
	PING = 'ping',
	PONG = 'pong',
}

export enum ConnectionHealthState {
	NEVER_CONNECTED = 'never_connected',
	CONNECTED = 'connected',
	RECONNECTING = 'reconnecting',
}

export type ConnectionDiagnosticsSnapshot = {
	connectionHealthState: ConnectionHealthState;
	reconnectCount: number;
	failedReconnectAttempts: number;
};

type ConnectionCloseContext = {
	closeCode?: number;
	closeReason?: string;
	wasClean?: boolean;
};

type WebSocketConnectionConfig = {
	url: string;
	onMessage?: (event: MessageEvent) => void;
};

const PING_PING_INTERVAL_MS = 3_000;
const PONG_TIMEOUT = 5_000;
const RECONNECT_DELAY_MS = 3_000;

export class WebSocketConnection {
	private webSocket: WebSocket;
	private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
	private pendingMessage: string | null = null;
	private onMessageCallback: ((event: MessageEvent) => void) | null = null;
	private readonly url: string;
	private connectionIssueReported = false;
	private connectionHealthState: ConnectionHealthState = ConnectionHealthState.NEVER_CONNECTED;
	private failedReconnectAttempts = 0;
	private reconnectCount = 0;
	private lastPongTimestamp = Date.now();
	private lastPingRoundTripMs: number | undefined = undefined;
	private readonly _handleOnline = (): void => this._verifyConnectionHealth();
	private readonly _handleVisibilityChange = (): void => {
		if (document.visibilityState === 'visible') {
			this._verifyConnectionHealth();
		}
	};

	constructor(config: WebSocketConnectionConfig) {
		this.url = config.url;
		this.onMessageCallback = config.onMessage ?? null;
		this.webSocket = this._createWebSocket();
		this._bindHandlers();
		this._registerConnectivityListeners();
	}

	destroy(): void {
		this._stopPingPong();
		window.removeEventListener('online', this._handleOnline);
		document.removeEventListener('visibilitychange', this._handleVisibilityChange);
		this.webSocket.onopen = null;
		this.webSocket.onclose = null;
		this.webSocket.onerror = null;
		this.webSocket.onmessage = null;
		this.webSocket.close();
	}

	send(message: string): void {
		if (this.webSocket.readyState === WebSocket.OPEN) {
			this.webSocket.send(message);
			this.pendingMessage = null;
		} else {
			this.pendingMessage = message;
			logError('WebSocket is not connected');
		}
	}

	setOnMessage(callback: (event: MessageEvent) => void): void {
		this.onMessageCallback = callback;
	}

	getDiagnosticsSnapshot(): ConnectionDiagnosticsSnapshot {
		return {
			connectionHealthState: this.connectionHealthState,
			reconnectCount: this.reconnectCount,
			failedReconnectAttempts: this.failedReconnectAttempts,
		};
	}

	private _createWebSocket(): WebSocket {
		return new WebSocket(this.url);
	}

	private _bindHandlers(): void {
		this.webSocket.onopen = (): void => {
			this.connectionIssueReported = false;
			this.lastPongTimestamp = Date.now();
			this._startPingPong();
			const isReconnect = this.connectionHealthState === ConnectionHealthState.RECONNECTING;
			if (isReconnect) {
				this.reconnectCount += 1;
				notifyReconnected();
			}
			this.connectionHealthState = ConnectionHealthState.CONNECTED;
			this.failedReconnectAttempts = 0;
			if (this.pendingMessage !== null) {
				this.webSocket.send(this.pendingMessage);
				this.pendingMessage = null;
			}
		};
		this.webSocket.onclose = (event: CloseEvent): void => {
			this._stopPingPong();
			this._handleConnectionProblem({
				closeCode: event.code,
				closeReason: event.reason,
				wasClean: event.wasClean,
			});
			setTimeout((): void => {
				this.webSocket = this._createWebSocket();
				this._bindHandlers();
			}, RECONNECT_DELAY_MS);
		};
		this.webSocket.onmessage = (event: MessageEvent): void => {
			let parsedMessage: { type?: string; timestamp?: number } | null = null;
			try {
				parsedMessage = JSON.parse(event.data);
			} catch {
				parsedMessage = null;
			}
			if (parsedMessage?.type === HeartbeatMessageType.PONG) {
				this.lastPongTimestamp = Date.now();
				if (typeof parsedMessage.timestamp === 'number') {
					this.lastPingRoundTripMs = Date.now() - parsedMessage.timestamp;
				}
				return;
			}
			this.onMessageCallback?.(event);
		};
		this.webSocket.onerror = (): void => { };
	}

	private _startPingPong(): void {
		this.heartbeatInterval = setInterval((): void => {
			const timeSinceLastPong = Date.now() - this.lastPongTimestamp;
			if (timeSinceLastPong > PONG_TIMEOUT) {
				this._handleConnectionProblem({ wasClean: false });
				this._forceReconnect();
				return;
			}
			if (this.webSocket.readyState === WebSocket.OPEN) {
				this.webSocket.send(JSON.stringify({ type: HeartbeatMessageType.PING, timestamp: Date.now() }));
			}
		}, PING_PING_INTERVAL_MS);
	}

	private _stopPingPong(): void {
		if (this.heartbeatInterval !== null) {
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}
	}

	private _forceReconnect(): void {
		this._stopPingPong();
		const staleWebSocket = this.webSocket;
		staleWebSocket.onopen = null;
		staleWebSocket.onclose = null;
		staleWebSocket.onerror = null;
		staleWebSocket.onmessage = null;
		staleWebSocket.close();
		this.webSocket = this._createWebSocket();
		this._bindHandlers();
	}

	private _registerConnectivityListeners(): void {
		window.addEventListener('online', this._handleOnline);
		document.addEventListener('visibilitychange', this._handleVisibilityChange);
	}

	private _verifyConnectionHealth(): void {
		if (this.webSocket.readyState === WebSocket.CONNECTING) {
			return;
		}
		const timeSinceLastPong = Date.now() - this.lastPongTimestamp;
		const isSocketOpen = this.webSocket.readyState === WebSocket.OPEN;
		const missedLastPong = isSocketOpen && timeSinceLastPong > PONG_TIMEOUT;
		if (!isSocketOpen || missedLastPong) {
			this._handleConnectionProblem({ wasClean: false });
			this._forceReconnect();
		}
	}

	private _handleConnectionProblem(context: ConnectionCloseContext): void {
		if (this.connectionHealthState === ConnectionHealthState.CONNECTED) {
			this.connectionHealthState = ConnectionHealthState.RECONNECTING;
			this.failedReconnectAttempts = 0;
			notifyConnectionLost();
		} else if (this.connectionHealthState === ConnectionHealthState.RECONNECTING) {
			this.failedReconnectAttempts += 1;
		}

		if (this.connectionHealthState === ConnectionHealthState.RECONNECTING) {
			notifyReconnecting(this.failedReconnectAttempts + 1);
		}

		if (!this.connectionIssueReported) {
			this.connectionIssueReported = true;
			void new WebSocketDiagnostics(this.url, {
				closeCode: context.closeCode,
				closeReason: context.closeReason,
				wasClean: context.wasClean,
				timeSinceLastPongMs: Date.now() - this.lastPongTimestamp,
				pingRoundTripMs: this.lastPingRoundTripMs,
				reconnectCount: this.reconnectCount,
			}).run();
		}
	}
}
